import React from 'react';
import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { getSafeHTML } from 'utils';

import { IQuiz } from '../quiz.types';
import {
  StyledAnswerAgainButton,
  StyledConfirmationMessage,
  StyledFormCard,
  StyledProgressDot,
  StyledProgressSegment,
  StyledProgressTrack,
  StyledStepCounter,
  StyledStepHeader,
} from '../styles';
import {
  QuizContext,
  QuizContextType,
  UseQuizFormReturn,
} from '../useQuizForm';
import { QuizNavigationCheckDialog } from './NavigationCheckDialog';
import { QuizNavigation } from './QuizNavigation';
import { QuizStep } from './QuizStep';
import {
  StyledAnswerAgainWrapper,
  StyledHiddenSubmitButton,
  StyledStepPanel,
  StyledSubmitError,
  StyledSuccessMessage,
} from './styles';

type QuizFormProps = {
  quiz: IQuiz;
  quizForm: UseQuizFormReturn;
  isAllowedToAnswerAgain: boolean;
};

/**
 * Renders the step-by-step quiz form bloc below the description card.
 * All RHF logic lives in `useQuizForm`; this component is purely presentational.
 */
export function QuizForm({
  quiz,
  quizForm,
  isAllowedToAnswerAgain,
}: QuizFormProps): JSX.Element {
  const { t } = useTranslation();

  const {
    currentStep,
    steps,
    totalSteps,
    isFirstStep,
    isLastStep,
    goNext,
    goPrev,
    goToStep,
    form,
    handleSubmit,
    isSubmitting,
    isSubmitSuccess,
    isSubmitError,
    isCurrentStepValid,
    validatedSteps,
    openConfirmDialog,
    setOpenConfirmDialog,
    resetQuiz,
  } = quizForm;

  const currentGroup = steps[currentStep];

  // ---- Validate current step fields before navigating forward -------------
  const handleNext = async () => {
    const allInputs = currentGroup.formInputs;

    if (allInputs.length === 0) {
      goNext();
      return;
    }

    const results = await Promise.all(
      allInputs.map((input) => {
        if (input.type === 'text')
          return form.trigger(`answers.${input.id}.value` as any);
        if (input.type === 'upload')
          return form.trigger(`answers.${input.id}.file` as any);
        return form.trigger(`answers.${input.id}.choiceValues` as any);
      }),
    );

    if (results.every(Boolean)) goNext();
  };

  // ---- Context value -------------------------------------------------------
  const quizContextValue: QuizContextType = {
    currentStep,
    totalSteps,
    isFirstStep,
    isLastStep,
    goPrevStep: goPrev,
    goNextStep: handleNext,
    isCurrentStepValid,
    isSubmitting,
    openConfirmDialog,
    setOpenConfirmDialog,
  };

  // ---- Post-submit success screen -----------------------------------------
  const safeConfirmation =
    isSubmitSuccess && quiz.confirmationMessage
      ? getSafeHTML(quiz.confirmationMessage)
      : null;

  // ---- Quiz validity period -------------------------------------------------
  const now = new Date();
  const isBeforeStart = now < new Date(quiz.startDateTime);
  const isAfterEnd = !!quiz.endDateTime && now >= new Date(quiz.endDateTime);

  return (
    <React.Fragment>
      {isSubmitSuccess && (
        <StyledFormCard>
          {safeConfirmation ? (
            <StyledConfirmationMessage
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: safeConfirmation }}
            />
          ) : (
            <StyledSuccessMessage variant="h5" align="center">
              {t('quiz.form.submitSuccess')}
            </StyledSuccessMessage>
          )}
          {isAllowedToAnswerAgain && (
            <StyledAnswerAgainWrapper>
              <StyledAnswerAgainButton
                variant="outlined"
                onClick={resetQuiz}
                startIcon={<span aria-hidden>↩</span>}
              >
                {t('quiz.form.answerAgain')}
              </StyledAnswerAgainButton>
            </StyledAnswerAgainWrapper>
          )}
        </StyledFormCard>
      )}

      {!isSubmitSuccess && (isBeforeStart || isAfterEnd) && (
        <StyledFormCard>
          <StyledSuccessMessage variant="h5" align="center">
            {t(isBeforeStart ? 'quiz.notOpenYet' : 'quiz.closed')}
          </StyledSuccessMessage>
        </StyledFormCard>
      )}

      {isAllowedToAnswerAgain &&
        !isSubmitSuccess &&
        !isBeforeStart &&
        !isAfterEnd && (
          <QuizContext.Provider value={quizContextValue}>
            <FormProvider {...form}>
              {/* Navigation guard (react-router + beforeunload) */}
              <QuizNavigationCheckDialog />

              <StyledFormCard>
                <React.Fragment>
                  {/* ---- Step counter header ---- */}
                  <StyledStepHeader>
                    <StyledStepCounter variant="body1">
                      {t('quiz.form.stepCounter', {
                        current: currentStep + 1,
                        total: totalSteps,
                      })}
                    </StyledStepCounter>
                  </StyledStepHeader>

                  {/* ---- Progress timeline ---- */}
                  <StyledProgressTrack>
                    {totalSteps === 1 ? (
                      <React.Fragment>
                        <StyledProgressSegment done />
                        <StyledProgressDot active done />
                      </React.Fragment>
                    ) : (
                      steps.map((_, index) => (
                        <React.Fragment key={index}>
                          <StyledProgressDot
                            active={index === currentStep}
                            done={index < currentStep}
                            clickable={
                              validatedSteps.has(index) && !isSubmitting
                            }
                            onClick={() => {
                              if (validatedSteps.has(index) && !isSubmitting) {
                                goToStep(index);
                              }
                            }}
                            title={
                              validatedSteps.has(index)
                                ? t('quiz.form.stepCounter', {
                                    current: index + 1,
                                    total: totalSteps,
                                  })
                                : undefined
                            }
                          />
                          {index < totalSteps - 1 && (
                            <StyledProgressSegment done={index < currentStep} />
                          )}
                        </React.Fragment>
                      ))
                    )}
                  </StyledProgressTrack>

                  {/* ---- Current question(s) ---- */}
                  <form onSubmit={handleSubmit} noValidate>
                    {/*
              Render ALL steps but only display the active one.
              This keeps every input mounted and registered in RHF so values
              are preserved when navigating back to a previous step.
            */}
                    {steps.map((group, index) => (
                      <StyledStepPanel
                        key={group.id}
                        visible={index === currentStep}
                      >
                        <QuizStep group={group} />
                      </StyledStepPanel>
                    ))}

                    {/* ---- Submit error ---- */}
                    {isSubmitError && (
                      <StyledSubmitError color="error" variant="body2">
                        {t('quiz.form.submitError')}
                      </StyledSubmitError>
                    )}

                    {/* ---- Sticky round navigation (mirrors DeclarationNavigation) ---- */}
                    <QuizNavigation />

                    {/* Hidden submit button triggered by QuizNavigation on last step confirm */}
                    <StyledHiddenSubmitButton
                      type="submit"
                      id="quiz-submit-btn"
                    />
                  </form>
                </React.Fragment>
              </StyledFormCard>
            </FormProvider>
          </QuizContext.Provider>
        )}
    </React.Fragment>
  );
}
